// Taki taslaklarinin fiyatlarini urunun guncel maliyet + kargosuna gore
// toplu gunceller. Acilinca hesaplar, degisecekleri eski -> yeni gosterir;
// onaylaninca taslaklari (ve istenirse Etsy'deki ilanlarin envanterini) yazar.
// Hesap kurali: lib/priceRecalc.ts (Liste Denetimi ile ayni formul).

import { useEffect, useMemo, useState } from "react";
import { X, Loader2, ArrowRight, AlertTriangle, CheckCircle2 } from "lucide-react";
import type { SupplierCatalog } from "@erc/shared";
import { apiClient } from "../../lib/api";
import { authHeaders, getBackendUrl } from "../../lib/http";
import { isBoundHere } from "../../lib/listingDrafts";
import { LIST_PRICE_FACTOR, type TemplateRow, type OptionSet } from "../../lib/jewelryListing";
import { findProduct, planPriceUpdate, type FullDraft, type MultiplierSource, type PricePlan } from "../../lib/priceRecalc";

const money = (n: number | null | undefined) => (n == null ? "—" : `$${n.toFixed(2)}`);
const range = (r: [number, number] | null) => (!r ? "—" : r[0] === r[1] ? money(r[0]) : `${money(r[0])}–${money(r[1])}`);

function Change({ from, to, fmt = money }: { from: number | null; to: number | null; fmt?: (n: number | null) => string }) {
  const same = from != null && to != null && Math.abs(from - to) < 0.005;
  if (same) return <span className="text-gray-500">{fmt(to)}</span>;
  const up = from != null && to != null && to > from;
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      <span className="text-gray-400 line-through">{fmt(from)}</span>
      <ArrowRight className="h-3 w-3 text-gray-400" />
      <span className={`font-medium ${from == null ? "text-gray-900" : up ? "text-emerald-700" : "text-red-600"}`}>{fmt(to)}</span>
    </span>
  );
}

export function PriceRecalcModal({
  visibleIds, activeShopId, onClose, onDone,
}: {
  /** Listede gorunen taslaklar (magaza/dikey suzgeci uygulanmis) */
  visibleIds: string[];
  activeShopId: string | null;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const [data, setData] = useState<{
    drafts: FullDraft[]; catalogs: SupplierCatalog[]; templates: TemplateRow[]; optionSets: OptionSet[];
  } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [multiplierSource, setMultiplierSource] = useState<MultiplierSource>("draft");
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [pushEtsy, setPushEtsy] = useState(true);
  const [applying, setApplying] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [failures, setFailures] = useState<string[]>([]);
  const [showSkipped, setShowSkipped] = useState(false);

  // Guncel maliyet icin kataloglar her acilista taze cekilir (onbellek degil)
  useEffect(() => {
    const json = (path: string) =>
      fetch(`${getBackendUrl()}${path}`, { headers: authHeaders() }).then((r) => {
        if (!r.ok) throw new Error(`${path} alınamadı (${r.status})`);
        return r.json();
      });
    Promise.all([
      json("/api/listing-drafts"),
      apiClient.listSupplierCatalogs("jewelry"),
      json("/api/listing-templates"),
      json("/api/option-sets"),
    ])
      .then(([d, catalogs, t, o]) => setData({
        drafts: (d?.data ?? []) as FullDraft[], catalogs, templates: t?.data ?? [], optionSets: o?.data ?? [],
      }))
      .catch((e) => setLoadError(e instanceof Error ? e.message : String(e)));
  }, []);

  const plans = useMemo<PricePlan[]>(() => {
    if (!data) return [];
    const visible = new Set(visibleIds);
    return data.drafts
      .filter((d) => visible.has(d.id) && d.status !== "archived")
      .filter((d) => (d.attributes as { supplier?: { categoryId?: string } } | null)?.supplier?.categoryId)
      .map((draft) => {
        const s = (draft.attributes as { supplier: { catalogId?: string; categoryId?: string } }).supplier;
        return planPriceUpdate({
          draft, product: findProduct(data.catalogs, s.catalogId, s.categoryId),
          templates: data.templates, optionSets: data.optionSets, multiplierSource,
        });
      });
  }, [data, visibleIds, multiplierSource]);

  const changed = plans.filter((p) => p.changed);
  const skipped = plans.filter((p) => p.skip);
  const upToDate = plans.length - changed.length - skipped.length;
  const selected = changed.filter((p) => !excluded.has(p.draft.id));
  const etsyCount = selected.filter((p) => isBoundHere(p.draft, activeShopId)).length;

  const toggle = (id: string) => setExcluded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  async function apply() {
    setApplying(true);
    setFailures([]);
    const fails: string[] = [];
    let saved = 0;
    let pushed = 0;
    for (const [i, p] of selected.entries()) {
      setProgress(`${i + 1}/${selected.length} · ${p.draft.title.slice(0, 50)}`);
      try {
        const res = await fetch(`${getBackendUrl()}/api/listing-drafts/${p.draft.id}`, {
          method: "PATCH",
          headers: { ...authHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify(p.patch),
        });
        if (!res.ok) throw new Error(`taslak kaydedilemedi (${res.status})`);
        saved++;
        if (pushEtsy && isBoundHere(p.draft, activeShopId)) {
          // Envanter kayitli taslaktan kurulur; fiyat varyasyonsuz ilanda da gitsin diye force
          const inv = await fetch(`${getBackendUrl()}/api/etsy/listings/${p.draft.etsyListingId}/inventory-from-draft`, {
            method: "POST",
            headers: { ...authHeaders(), "Content-Type": "application/json" },
            // Yalnizca fiyat: kategori ozelliklerini yeniden yazmaya gerek yok
            body: JSON.stringify({ listingDraftId: p.draft.id, force: true, properties: false }),
          });
          if (!inv.ok) {
            const b = await inv.json().catch(() => ({}));
            throw new Error(`taslak kaydedildi ama Etsy güncellenemedi: ${b.error || inv.status}`);
          }
          pushed++;
        }
      } catch (e) {
        fails.push(`${p.draft.title.slice(0, 60)} — ${e instanceof Error ? e.message : String(e)}`);
      }
    }
    setApplying(false);
    setProgress(null);
    const msg = `${saved} taslağın fiyatı güncellendi${pushed ? `, ${pushed} Etsy ilanına gönderildi` : ""}${fails.length ? ` · ${fails.length} hata` : ""}.`;
    if (fails.length) setFailures(fails);
    else onDone(msg);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !applying && onClose()}>
      <div className="flex max-h-[90vh] w-full max-w-5xl flex-col rounded-xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between border-b px-5 py-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Fiyatları güncelle</h3>
            <p className="mt-0.5 text-xs text-gray-500">
              Ürünlerin güncel maliyet + kargosuyla yeniden hesaplanır: satış fiyatı, tüm Etsy ücretleri düşüldükten sonra hedef kâr kalacak şekilde; Etsy liste fiyatı = satış × {LIST_PRICE_FACTOR}.
            </p>
          </div>
          <button onClick={onClose} disabled={applying} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex flex-wrap items-center gap-3 border-b bg-gray-50 px-5 py-2.5 text-xs">
          <span className="font-medium text-gray-700">Fiyat kuralı:</span>
          {([
            ["draft", "Taslaktakini koru (eski çarpanlılar çarpanla kalır)"],
            ["rule", "Hedef kâr % uygula (şablon, yoksa %20)"],
          ] as const).map(([id, label]) => (
            <label key={id} className="flex items-center gap-1.5">
              <input type="radio" checked={multiplierSource === id} onChange={() => setMultiplierSource(id)} disabled={applying} />
              {label}
            </label>
          ))}
          {data && (
            <span className="ml-auto text-gray-500">
              <b className="text-gray-900">{changed.length}</b> değişecek · {upToDate} güncel · {skipped.length} atlandı
            </span>
          )}
        </div>

        <div className="min-h-[200px] flex-1 overflow-y-auto px-5 py-3">
          {!data && !loadError && (
            <div className="flex h-40 items-center justify-center gap-2 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Hesaplanıyor…</div>
          )}
          {loadError && <p className="text-sm text-red-600">{loadError}</p>}
          {data && changed.length === 0 && (
            <div className="flex h-40 flex-col items-center justify-center gap-2 text-sm text-gray-500">
              <CheckCircle2 className="h-6 w-6 text-emerald-500" /> Tüm fiyatlar güncel.
            </div>
          )}
          {changed.length > 0 && (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white text-left text-gray-500">
                <tr className="border-b">
                  <th className="py-2 pr-2">
                    <input type="checkbox" checked={excluded.size === 0} disabled={applying}
                      onChange={(e) => setExcluded(e.target.checked ? new Set() : new Set(changed.map((p) => p.draft.id)))} />
                  </th>
                  <th className="py-2 pr-3">Taslak</th>
                  <th className="py-2 pr-3">Maliyet + kargo</th>
                  <th className="py-2 pr-3">Kural</th>
                  <th className="py-2 pr-3">Liste fiyatı</th>
                  <th className="py-2 pr-3">Varyasyonlar</th>
                  <th className="py-2 pr-3">En ucuz</th>
                  <th className="py-2">Kâr</th>
                </tr>
              </thead>
              <tbody>
                {changed.map((p) => {
                  const oldBase = p.oldCost != null && p.oldShipping != null ? p.oldCost + p.oldShipping : null;
                  return (
                    <tr key={p.draft.id} className={`border-b align-top ${excluded.has(p.draft.id) ? "opacity-40" : ""}`}>
                      <td className="py-2 pr-2"><input type="checkbox" checked={!excluded.has(p.draft.id)} onChange={() => toggle(p.draft.id)} disabled={applying} /></td>
                      <td className="max-w-[240px] py-2 pr-3">
                        <div className="flex items-center gap-2">
                          {p.draft.imageUrls?.[0] && <img src={p.draft.imageUrls[0].startsWith("http") ? p.draft.imageUrls[0] : `${getBackendUrl()}${p.draft.imageUrls[0]}`} alt="" className="h-8 w-8 flex-shrink-0 rounded object-cover" />}
                          <div className="min-w-0">
                            <p className="truncate text-gray-900" title={p.draft.title}>{p.draft.title}</p>
                            {isBoundHere(p.draft, activeShopId) && <span className="text-[10px] font-medium text-orange-600">Etsy'de</span>}
                            {p.unmatched > 0 && <p className="text-[10px] text-amber-700">{p.unmatched} seçenek eşleşmedi, eski fiyatla kalır</p>}
                          </div>
                        </div>
                      </td>
                      <td className="py-2 pr-3"><Change from={oldBase} to={p.newCost + p.newShipping} /></td>
                      <td className="py-2 pr-3 whitespace-nowrap">
                        {p.oldRule === p.newRule ? <span className="text-gray-500">{p.newRule}</span> : (
                          <span className="inline-flex items-center gap-1">
                            <span className="text-gray-400 line-through">{p.oldRule ?? "—"}</span>
                            <ArrowRight className="h-3 w-3 text-gray-400" />
                            <span className="font-medium text-gray-900">{p.newRule}</span>
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-3"><Change from={p.oldPrice} to={p.newPrice} /></td>
                      <td className="py-2 pr-3 whitespace-nowrap">
                        {p.oldRange || p.newRange ? (
                          <span className="inline-flex items-center gap-1">
                            <span className="text-gray-400">{range(p.oldRange)}</span>
                            <ArrowRight className="h-3 w-3 text-gray-400" />
                            <span className="font-medium text-gray-900">{range(p.newRange)}</span>
                          </span>
                        ) : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="py-2 pr-3">{p.oldEntry != null || p.newEntry != null ? <Change from={p.oldEntry} to={p.newEntry} /> : <span className="text-gray-400">—</span>}</td>
                      <td className={`py-2 whitespace-nowrap ${p.profit <= 0 ? "font-medium text-red-600" : "text-gray-700"}`}>
                        {money(p.profit)} <span className="text-[10px] text-gray-500">%{p.marginPct.toFixed(1)}</span>
                        {p.entryProfit != null && (
                          <span className={`block text-[10px] ${p.entryProfit <= 0 ? "text-red-600" : "text-gray-500"}`}>en ucuz: {money(p.entryProfit)}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {skipped.length > 0 && (
            <div className="mt-3">
              <button onClick={() => setShowSkipped((v) => !v)} className="text-xs text-gray-500 hover:text-gray-700">
                {showSkipped ? "▾" : "▸"} {skipped.length} taslak hesaplanamadı
              </button>
              {showSkipped && (
                <ul className="mt-1 space-y-0.5 text-[11px] text-gray-500">
                  {skipped.map((p) => <li key={p.draft.id}>{p.draft.title.slice(0, 70)} — {p.skip}</li>)}
                </ul>
              )}
            </div>
          )}

          {failures.length > 0 && (
            <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
              <p className="mb-1 flex items-center gap-1 font-medium"><AlertTriangle className="h-3.5 w-3.5" /> Bazıları güncellenemedi</p>
              <ul className="space-y-0.5">{failures.map((f) => <li key={f}>{f}</li>)}</ul>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t px-5 py-3">
          <label className={`flex items-center gap-1.5 text-xs ${etsyCount ? "text-gray-700" : "text-gray-400"}`}>
            <input type="checkbox" checked={pushEtsy} onChange={(e) => setPushEtsy(e.target.checked)} disabled={applying || !etsyCount} />
            Etsy'deki ilanlara da uygula ({etsyCount})
          </label>
          <div className="flex items-center gap-2">
            {progress && <span className="text-xs text-gray-500">{progress}</span>}
            <button onClick={failures.length ? () => onDone("Fiyat güncellemesi tamamlandı, bazı hatalar var.") : onClose} disabled={applying}
              className="rounded-lg border px-3 py-2 text-xs text-gray-600 hover:bg-gray-50">
              {failures.length ? "Kapat" : "Vazgeç"}
            </button>
            <button onClick={() => void apply()} disabled={applying || selected.length === 0 || failures.length > 0}
              className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-40">
              {applying && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {selected.length} taslağı güncelle
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
